/* ==========================================================================
   sk-collection.js — Kollektion + Suche (Horizon-Port 30.10.2026)
   Web Component <sk-collection-grid>: Filter, Sortierung und Seitenwechsel per
   Section Rendering API (?section_id=…) statt Neuladen der Seite. Die Scrollposition
   bleibt beim Filtern erhalten (kein Sprung an den Seitenanfang), beim Seitenwechsel
   scrollt die Ansicht an den Anfang des Rasters. URL wird per history.pushState
   aktualisiert, Zurück/Vor lädt den passenden Stand.
   Dazu: „Weiterlesen“ in der Kollektionsbeschreibung, Filter-Drawer (mobil),
   lange Textblöcke einklappen ([data-sk-longtext]), absolute silberkrafto.shop-Links
   in Metafeld-Inhalten relativ machen.
   Kein jQuery, keine Abhängigkeit zu Horizon-Komponenten.
   ========================================================================== */

const SKV2_REGIONS = ['count', 'filters', 'grid', 'filtercount', 'active'];

function skv2Relativize(root) {
  root.querySelectorAll('a[data-skv2-ajax][href*="section_id="]').forEach((a) => {
    const u = new URL(a.getAttribute('href'), window.location.origin);
    u.searchParams.delete('section_id');
    a.setAttribute('href', u.pathname + u.search + u.hash);
  });
  root.querySelectorAll('a[href]').forEach((a) => {
    const m = a.getAttribute('href').match(/^https?:\/\/(www\.)?silberkraft\.com(\/[^#]*)?(#.*)?$/i);
    if (m) a.setAttribute('href', (m[2] || '/') + (m[3] || ''));
  });
}

class SkCollectionGrid extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.labelMore = this.dataset.labelMore || '';
    this.labelLess = this.dataset.labelLess || '';
    this.pending = null;
    this.controller = null;

    this.setupDescToggle();
    this.setupDrawer();
    skv2Relativize(this);

    this.addEventListener('change', (e) => this.onChange(e));
    this.addEventListener('submit', (e) => {
      if (e.target.matches('[data-skv2-filters]')) { e.preventDefault(); this.applyForm(0); }
    });
    this.addEventListener('click', (e) => this.onClick(e));
    this.onPop = () => this.load(new URL(window.location.href), { push: false, scroll: false });
    window.addEventListener('popstate', this.onPop);
    this.updateFilterCount();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPop);
  }

  /* ---------- „Weiterlesen“ in der Beschreibung ---------- */
  setupDescToggle() {
    const d = this.querySelector('[data-skv2-desc]');
    if (!d || d.dataset.toggleReady) return;
    d.dataset.toggleReady = '1';
    const collapsed = d.clientHeight;
    d.classList.add('is-expanded');
    const expanded = d.clientHeight;
    d.classList.remove('is-expanded');
    if (expanded - collapsed < 8) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'skv2-hero__more';
    btn.textContent = this.labelMore;
    btn.setAttribute('aria-expanded', 'false');
    btn.addEventListener('click', () => {
      const open = d.classList.toggle('is-expanded');
      btn.textContent = open ? this.labelLess : this.labelMore;
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    d.after(btn);
  }

  /* ---------- Filter-Drawer (mobil) ---------- */
  setupDrawer() {
    this.sidebar = this.querySelector('[data-skv2-sidebar]');
    this.backdrop = this.querySelector('[data-skv2-sidebar-backdrop]');
    this.onKey = (e) => { if (e.key === 'Escape') this.closeDrawer(); };
    document.addEventListener('keydown', this.onKey);
  }

  openDrawer() {
    if (!this.sidebar) return;
    /* Desktop (> 900 px): Sidebar steht neben dem Raster – Button springt nur dorthin */
    if (!window.matchMedia('(max-width: 900px)').matches) {
      this.sidebar.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      return;
    }
    this.sidebar.classList.add('is-open');
    if (this.backdrop) {
      this.backdrop.hidden = false;
      requestAnimationFrame(() => this.backdrop.classList.add('is-open'));
    }
    this.querySelectorAll('[data-skv2-filter-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'true'));
    document.documentElement.setAttribute('scroll-lock', '');
    const close = this.sidebar.querySelector('[data-skv2-sidebar-close]');
    if (close) close.focus({ preventScroll: true });
  }

  closeDrawer() {
    if (!this.sidebar || !this.sidebar.classList.contains('is-open')) return;
    this.sidebar.classList.remove('is-open');
    if (this.backdrop) {
      this.backdrop.classList.remove('is-open');
      setTimeout(() => { this.backdrop.hidden = true; }, 200);
    }
    this.querySelectorAll('[data-skv2-filter-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    document.documentElement.removeAttribute('scroll-lock');
  }

  /* ---------- Events ---------- */
  onChange(e) {
    const t = e.target;
    if (t.matches('[data-skv2-sort]')) {
      const url = this.currentUrl();
      url.searchParams.set('sort_by', t.value);
      url.searchParams.delete('page');
      this.load(url, { push: true, scroll: false });
      return;
    }
    if (!t.closest('[data-skv2-filters]')) return;
    clearTimeout(this.pending);
    const delay = t.matches('input[type="number"]') ? 800 : 150;
    this.pending = setTimeout(() => this.applyForm(), delay);
  }

  onClick(e) {
    const toggle = e.target.closest('[data-skv2-filter-toggle]');
    if (toggle) { e.preventDefault(); this.openDrawer(); return; }
    if (e.target.closest('[data-skv2-sidebar-close]') || e.target.closest('[data-skv2-sidebar-backdrop]')) {
      e.preventDefault(); this.closeDrawer(); return;
    }
    const a = e.target.closest('a[data-skv2-ajax]');
    if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    const isPage = a.hasAttribute('data-skv2-page');
    this.load(new URL(a.href, window.location.origin), { push: true, scroll: isPage });
  }

  currentUrl() {
    return new URL(window.location.href);
  }

  applyForm() {
    const form = this.querySelector('[data-skv2-filters]');
    if (!form) return;
    const url = this.currentUrl();
    [...url.searchParams.keys()].forEach((k) => {
      if (k.startsWith('filter.') || k === 'page') url.searchParams.delete(k);
    });
    for (const [k, v] of new FormData(form).entries()) {
      if (k.startsWith('filter.') && v !== '' && v != null) url.searchParams.append(k, v);
    }
    this.load(url, { push: true, scroll: false });
  }

  /* ---------- Section Rendering ---------- */
  async load(url, { push, scroll }) {
    if (!this.sectionId) { window.location.href = url.toString(); return; }
    url.searchParams.delete('section_id');
    const fetchUrl = new URL(url.toString());
    fetchUrl.searchParams.set('section_id', this.sectionId);
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    this.setAttribute('aria-busy', 'true');
    this.classList.add('is-loading');
    const openState = this.captureOpenState();
    let html;
    try {
      const res = await fetch(fetchUrl.toString(), { signal: this.controller.signal, credentials: 'same-origin' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      html = await res.text();
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      window.location.href = url.toString();
      return;
    } finally {
      this.removeAttribute('aria-busy');
      this.classList.remove('is-loading');
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('sk-collection-grid');
    if (!fresh) { window.location.href = url.toString(); return; }
    SKV2_REGIONS.forEach((name) => {
      const cur = this.querySelector(`[data-skv2-region="${name}"]`);
      const next = fresh.querySelector(`[data-skv2-region="${name}"]`);
      if (cur && next) cur.innerHTML = next.innerHTML;
      else if (cur && !next) cur.innerHTML = '';
    });
    this.restoreOpenState(openState);
    skv2Relativize(this);
    this.updateFilterCount();
    if (push) window.history.pushState({ skv2: true }, '', url.toString());
    if (scroll) {
      const target = this.querySelector('[data-skv2-scroll-target]') || this;
      target.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
    this.dispatchEvent(new CustomEvent('sk:collection:updated', { bubbles: true }));
  }

  captureOpenState() {
    const state = {};
    this.querySelectorAll('[data-skv2-filters] details[data-filter-key]').forEach((d) => {
      state[d.dataset.filterKey] = d.open;
    });
    return state;
  }

  restoreOpenState(state) {
    this.querySelectorAll('[data-skv2-filters] details[data-filter-key]').forEach((d) => {
      if (Object.prototype.hasOwnProperty.call(state, d.dataset.filterKey)) d.open = state[d.dataset.filterKey];
    });
  }

  updateFilterCount() {
    const form = this.querySelector('[data-skv2-filters]');
    const badges = this.querySelectorAll('[data-skv2-filter-count]');
    if (!badges.length) return;
    let n = 0;
    if (form) {
      n = form.querySelectorAll('input[type="checkbox"]:checked').length;
      const min = form.querySelector('input[name$="price.gte"]');
      const max = form.querySelector('input[name$="price.lte"]');
      if ((min && min.value) || (max && max.value)) n++;
    }
    badges.forEach((b) => { b.textContent = n; b.hidden = n === 0; });
  }
}

if (!customElements.get('sk-collection-grid')) customElements.define('sk-collection-grid', SkCollectionGrid);

/* ---------- Lange Texte unter dem Raster: Feld-Umbrüche, Links relativ, einklappen ---------- */
function skv2EnhanceLongtext() {
  const LABEL_TEST = /(Anwendungszeit|Anwendung|Wirkeintritt|Besonderheit|Wirkstoff|Wirkdauer)\s*:/;
  const LABEL_RE = /(<strong>)?\s*(Anwendungszeit|Anwendung|Wirkeintritt|Besonderheit|Wirkstoff|Wirkdauer)\s*(<\/strong>)?\s*:/g;
  document.querySelectorAll('[data-sk-longtext]').forEach((sec) => {
    if (sec.dataset.skLongReady) return;
    sec.dataset.skLongReady = '1';
    skv2Relativize(sec);
    sec.querySelectorAll('p').forEach((p) => {
      if (p.querySelector('a, img, iframe, script, input')) return;
      if (!LABEL_TEST.test(p.textContent)) return;
      const html = p.innerHTML;
      p.innerHTML = html.replace(LABEL_RE, (m, s1, label, s2, offset) => {
        const before = html.slice(0, offset).replace(/<[^>]+>/g, '').trim();
        return (before ? '<br>' : '') + '<strong>' + label + ':</strong>';
      });
    });
    if (sec.offsetHeight <= 900) return;
    const more = sec.dataset.labelMore || '';
    const less = sec.dataset.labelLess || '';
    sec.classList.add('skv2-collapsible');
    const bar = document.createElement('div');
    bar.className = 'skv2-collapse-bar';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'skv2-collapse-btn';
    btn.textContent = more;
    btn.setAttribute('aria-expanded', 'false');
    btn.addEventListener('click', () => {
      const open = sec.classList.toggle('is-open');
      btn.textContent = open ? less : more;
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (!open) sec.scrollIntoView({ block: 'start' });
    });
    bar.appendChild(btn);
    sec.after(bar);
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', skv2EnhanceLongtext);
else skv2EnhanceLongtext();
